<template>
  <div class="flex flex-col gap-4 w-full">
    <!-- Results header -->
    <div class="flex items-center gap-3">
      <button class="p-1 rounded-lg text-icon-normal transition-colors hover:bg-neutral-25 hover:text-icon-muted"
      @click="$emit('back')">
        <ChevronRightIcon class="transform rotate-180" />
      </button>
      <div class="w-px h-4 bg-border-normal" />
      <div class="flex items-center gap-2 px-1">
        <BaseText type="body" size="sm" class="text-text-normal">
          Results for "{{ searchQuery }}"
        </BaseText>
        <BaseText v-if="!initialLoading" type="body" size="sm" class="text-text-subdued">
          {{ resultsCount?.toLocaleString() ?? 0 }} {{ resultsCount === 1 ? 'brand' : 'brands' }}
        </BaseText>
      </div>
    </div>

    <!-- Results table -->
    <div class="flex flex-col gap-1 p-1 rounded-xl border border-border-normal">
      <!-- Scrolls sideways when the table doesn't fit, with the brand and action columns pinned -->
      <div ref="scrollContainer" class="results-scroll-container overflow-x-auto"
      :class="{ 'can-scroll-left': canScrollLeft, 'can-scroll-right': canScrollRight }"
      @scroll.passive="updateScrollState">
        <!-- Every row (section labels and divider included) sits flush and has both pinned cells,
        so the pinned columns and their edge shadows run unbroken from the header down -->
        <div ref="scrollContent" class="results-table flex flex-col">
          <!-- Table header -->
          <div class="results-grid-template rounded-lg bg-neutral-25">
            <BaseText type="body" size="sm" class="sticky-start px-2 py-1 rounded-l-lg bg-neutral-25 text-text-normal">
              Brand
            </BaseText>
            <BaseText type="body" size="sm" class="px-3 py-1 text-text-normal">
              Socials
            </BaseText>
            <BaseText type="body" size="sm" class="px-3 py-1 text-text-normal">
              Status
            </BaseText>
            <BaseText type="body" size="sm" class="px-3 py-1 text-text-normal">
              Active Ads
            </BaseText>
            <BaseText type="body" size="sm" class="px-3 py-1 text-text-normal">
              Data since
            </BaseText>
            <div class="sticky-end self-stretch rounded-r-lg bg-neutral-25" />
          </div>

          <!-- Your subscriptions -->
          <template v-if="showSubscriptions">
            <div class="results-grid-template">
              <div class="sticky-start flex items-center gap-1 px-2 py-3 bg-white">
                <FolderIcon class="text-icon-normal" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  Your subscriptions
                </BaseText>
              </div>
              <div class="sticky-end self-stretch bg-white" />
            </div>
            <div class="flex flex-col">
              <div v-for="brand in subscriptionResults" :key="`subscription-${brand.key}`"
              class="results-grid-template">
                <div class="sticky-start flex items-center gap-3 px-2 py-2.5 min-w-0 bg-white">
                  <BrandAvatar :avatar-src="brand.avatar" :brand-name="brand.name" :width="28" :height="28" class="rounded-md" />
                  <BaseText type="label" size="sm" class="text-text-muted truncate">
                    {{ brand.displayName.name }}
                    <span v-if="brand.displayName.parentName" class="text-text-normal">({{ brand.displayName.parentName }})</span>
                  </BaseText>
                </div>
                <div v-if="brand.socialsPreview.platforms.length" class="flex items-center gap-1 px-3 py-2">
                  <component :is="platform.icon" v-for="platform in brand.socialsPreview.platforms"
                  :key="`subscription-${brand.key}-${platform.network}`" class="text-text-subdued flex-shrink-0" />
                  <BaseText v-if="brand.socialsPreview.hiddenCount" type="label" size="xs"
                  class="ml-1 py-0.5 px-1 rounded-md border border-border-normal bg-white text-text-muted">
                    +{{ brand.socialsPreview.hiddenCount }}
                  </BaseText>
                </div>
                <BaseText v-else type="body" size="sm" class="px-3 py-2 text-text-subdued">
                  &mdash;
                </BaseText>
                <div class="flex items-center px-3 py-2 min-w-0">
                  <div class="flex items-center p-1 rounded-md bg-neutral-25 min-w-0">
                    <FolderIcon :width="16" :height="16" class="text-icon-normal flex-shrink-0" />
                    <BaseText type="label" size="xs" class="px-1 text-text-normal truncate">
                      {{ getFolderName(brand) }}
                    </BaseText>
                  </div>
                </div>
                <div class="flex items-center gap-6 px-3 py-2 whitespace-nowrap">
                  <div class="flex items-center gap-2">
                    <div class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: brand.analytics?.active_count ? '#00A879' : '#DFE1E7' }" />
                    <BaseText type="body" size="sm" class="text-text-muted">
                      {{ brand.analytics?.active_count?.toLocaleString() || 0 }}
                      <span class="text-text-normal">Active</span>
                    </BaseText>
                  </div>
                  <div class="flex items-center gap-2">
                    <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" style="background-color: #DFE1E7;" />
                    <BaseText type="body" size="sm" class="text-text-muted">
                      {{ brand.analytics?.inactive_count?.toLocaleString() || 0 }}
                      <span class="text-text-normal">Inactive</span>
                    </BaseText>
                  </div>
                </div>
                <BaseText type="body" size="sm" class="px-3 py-2 text-text-normal">
                  {{ formatDate(brand.dataSince) }}
                </BaseText>
                <div class="sticky-end flex items-center justify-end self-stretch pl-4 pr-1.5 bg-white"
                :class="{ 'menu-open': expandedBrandOptions === brand.key }">
                  <div v-on-clickaway="() => closeBrandOptions(brand)" class="relative">
                    <button class="brand-menu-button p-1.5 rounded-md"
                    :class="expandedBrandOptions === brand.key ? 'active' : 'inactive'"
                    :disabled="loadingMenu === brand.key"
                    @click="toggleBrandOptions(brand)">
                      <BaseLoadingSpinnerCircle v-if="loadingMenu === brand.key" class="text-icon-normal" />
                      <KebabMenuIcon v-else />
                    </button>
                    <transition name="option">
                      <div v-if="expandedBrandOptions === brand.key"
                      class="brand-options flex flex-col gap-0.5 p-1 rounded-lg bg-white">
                        <!-- Pending brands have no brand page until they finish processing -->
                        <button v-if="!brand.pending"
                        class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                        @click="expandedBrandOptions = null, viewBrand(brand)">
                          <ViewIcon class="transition-colors text-icon-normal group-hover:text-icon-muted" />
                          <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-text-muted">
                            View Brand
                          </BaseText>
                        </button>
                        <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                        @click="expandedBrandOptions = null, showChangeBrandFolderModal(brand)">
                          <ChangeFolderIcon class="transition-colors text-icon-normal group-hover:text-icon-muted" />
                          <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-text-muted">
                            Move
                          </BaseText>
                        </button>
                        <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                        @click="expandedBrandOptions = null, removeSpyderBrand(brand)">
                          <DeleteIcon class-name="transition-colors text-icon-normal group-hover:text-icon-muted" />
                          <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-text-muted">
                            Remove
                          </BaseText>
                        </button>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
            <div class="section-divider results-grid-template h-4">
              <div class="sticky-start self-stretch" />
              <div class="sticky-end self-stretch" />
            </div>
          </template>

          <!-- All brands -->
          <div class="results-grid-template">
            <div class="sticky-start flex items-center gap-1 px-2 py-3 bg-white">
              <GlobeIcon class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted">
                All brands
              </BaseText>
            </div>
            <div class="sticky-end self-stretch bg-white" />
          </div>
          <div v-if="!initialLoading && allBrandResults.length" class="flex flex-col transition-opacity"
          :class="{ 'opacity-50 pointer-events-none': loadingBrands }">
            <div v-for="(brand, index) in allBrandResults" :key="`brand-${brand.key}`"
            class="results-grid-template">
              <div class="sticky-start flex items-center gap-3 px-2 py-2.5 min-w-0 bg-white">
                <BrandAvatar :avatar-src="brand.avatar" :brand-name="brand.name" :width="28" :height="28" class="rounded-md" />
                <BaseText type="label" size="sm" class="text-text-muted truncate">
                  {{ brand.displayName.name }}
                  <span v-if="brand.displayName.parentName" class="font-normal text-text-normal">({{ brand.displayName.parentName }})</span>
                </BaseText>
              </div>
              <div v-if="brand.socialsPreview.platforms.length" class="flex items-center gap-1 px-3 py-2">
                <component :is="platform.icon" v-for="platform in brand.socialsPreview.platforms"
                :key="`brand-${brand.key}-${platform.network}`" class="text-text-subdued flex-shrink-0" />
                <BaseText v-if="brand.socialsPreview.hiddenCount" type="label" size="xs"
                class="ml-1 py-0.5 px-1 rounded-md border border-border-normal bg-white text-text-normal">
                  +{{ brand.socialsPreview.hiddenCount }}
                </BaseText>
              </div>
              <BaseText v-else type="body" size="sm" class="px-3 py-2 text-text-subdued">
                &mdash;
              </BaseText>
              <div class="flex items-center gap-2 px-3 py-2 whitespace-nowrap">
                <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="brand.adDataStatus.dotClass" />
                <BaseText type="body" size="sm" class="text-text-muted">
                  {{ brand.adDataStatus.label }}
                </BaseText>
              </div>
              <div class="flex items-center gap-6 px-3 py-2 whitespace-nowrap">
                <div class="flex items-center gap-2">
                  <div class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: brand.analytics?.active_count ? '#00A879' : '#DFE1E7' }" />
                  <BaseText type="body" size="sm" class="text-text-muted">
                    {{ brand.analytics?.active_count?.toLocaleString() || 0 }}
                    <span class="text-text-normal">Active</span>
                  </BaseText>
                </div>
                <div class="flex items-center gap-2">
                  <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" style="background-color: #DFE1E7;" />
                  <BaseText type="body" size="sm" class="text-text-muted">
                    {{ brand.analytics?.inactive_count?.toLocaleString() || 0 }}
                    <span class="text-text-normal">Inactive</span>
                  </BaseText>
                </div>
              </div>
              <BaseText type="body" size="sm" class="px-3 py-2 text-text-normal">
                {{ formatDate(brand.dataSince) }}
              </BaseText>
              <div class="sticky-end flex items-center justify-end self-stretch pl-4 pr-1.5 bg-white"
              :class="{ 'menu-open': expandedBrandOptions === brand.key }">
                <!-- Subscribed brands the search matched by something other than their name land here too -->
                <div v-if="brand.brand.is_subscribed" v-on-clickaway="() => closeBrandOptions(brand)" class="relative">
                  <button class="brand-menu-button p-1.5 rounded-md"
                  :class="expandedBrandOptions === brand.key ? 'active' : 'inactive'"
                  :disabled="loadingMenu === brand.key"
                  @click="toggleBrandOptions(brand)">
                    <BaseLoadingSpinnerCircle v-if="loadingMenu === brand.key" class="text-icon-normal" />
                    <KebabMenuIcon v-else />
                  </button>
                  <transition name="option">
                    <!-- The scroll container clips overflow, so the last row's menu opens upward -->
                    <div v-if="expandedBrandOptions === brand.key"
                    class="brand-options flex flex-col gap-0.5 p-1 rounded-lg bg-white"
                    :class="{ 'open-up': index === allBrandResults.length - 1 }">
                      <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                      @click="expandedBrandOptions = null, viewBrand(brand)">
                        <ViewIcon class="transition-colors text-icon-normal group-hover:text-icon-muted" />
                        <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-text-muted">
                          View Brand
                        </BaseText>
                      </button>
                      <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                      @click="expandedBrandOptions = null, showChangeBrandFolderModal(brand)">
                        <ChangeFolderIcon class="transition-colors text-icon-normal group-hover:text-icon-muted" />
                        <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-text-muted">
                          Move
                        </BaseText>
                      </button>
                      <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                      @click="expandedBrandOptions = null, removeSpyderBrand(brand)">
                        <DeleteIcon class-name="transition-colors text-icon-normal group-hover:text-icon-muted" />
                        <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-text-muted">
                          Remove
                        </BaseText>
                      </button>
                    </div>
                  </transition>
                </div>
                <BasePrimaryButton v-else text="View Brand" :icon="viewBrandIcon" @click="viewBrand(brand)" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Kept outside the scroll container so they stay centered/aligned in the visible card -->
      <div v-if="initialLoading" class="flex justify-center py-6">
        <BaseLoadingSpinnerCircle class="text-icon-normal" />
      </div>
      <BaseText v-else-if="!allBrandResults.length" type="body" size="sm" class="px-2 pb-3 text-text-subdued">
        No {{ showSubscriptions ? 'other ' : '' }}brands found for "{{ searchQuery }}"
      </BaseText>
      <!-- Pagination -->
      <template v-if="totalPages > 1">
        <div class="h-px mx-2 bg-border-normal" />
        <BasePagination class="p-2"
          :current-page="currentPage"
          :total-pages="totalPages"
          :disabled="loadingBrands"
          @change="fetchBrandResults"
        />
      </template>
    </div>

    <ChangeFolderModal
      v-if="showChangeFolderModal"
      :item="changeFolderBrand" item-type="brand"
      @close="showChangeFolderModal = false, changeFolderBrand = null"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import ForeplayAPI from '@/api/foreplayServer'
import { getBrandDisplayName, normalizeBrandsForSubscriptionsList } from '@/utils/spyder/brandUtils'
import { findFolderForBrand } from '@/utils/spyder/brandFolderUtils'
import { getSocialPlatformMonoIcon, getUniqueSocialNetworks } from '@/utils/spyder/brandSocialsUtils'

import BrandAvatar from '../brands/BrandAvatar.vue'
import ChangeFolderModal from '../globals/ChangeFolderModal.vue'

// Icons
import ChangeFolderIcon from '../globals/Icons/ChangeFolderIcon.vue'
import ChevronRightIcon from '../globals/Icons/ChevronRightIcon.vue'
import DeleteIcon from '../globals/Icons/DeleteIcon.vue'
import FolderIcon from '../globals/Icons/SpyderV2Icons/FolderIcon.vue'
import GlobeIcon from '../globals/Icons/GlobeIcon.vue'
import KebabMenuIcon from '../globals/Icons/KebabMenuIcon.vue'
import SpyderIcon from '../globals/Icons/SidebarIcons/SpyderIcon.vue'
import ViewIcon from '../globals/Icons/ViewIcon.vue'

const PAGE_SIZE = 20
// Page numbers can only reach the first 10,000 search results
const MAX_PAGES = Math.floor(10000 / PAGE_SIZE)
const MAX_SOCIAL_PREVIEWS = 3

const AD_DATA_STATUSES = {
  tracked: { label: 'Data Tracked', dotClass: 'bg-primary-green-100' },
  available: { label: 'Data Available', dotClass: 'bg-secondary-yellow-100' },
  none: { label: 'No Ad Library', dotClass: 'bg-neutral-100' }
}

// Tracked: we already hold ads for the brand. Available: it has a Meta ad library we can scrape once requested.
const getAdDataStatus = (brand) => {
  const { active_count: activeCount = 0, inactive_count: inactiveCount = 0 } = brand.analytics || {}
  if (activeCount + inactiveCount > 0) return AD_DATA_STATUSES.tracked
  const hasMetaAdLibrary = (brand.brand?.socials || []).some(social => social.network === 'facebook' && social.advertiser_id)
  return hasMetaAdLibrary ? AD_DATA_STATUSES.available : AD_DATA_STATUSES.none
}

// Unique platforms across the brand's socials, capped at MAX_SOCIAL_PREVIEWS with a count of the rest
const getSocialsPreview = (brand) => {
  const networks = getUniqueSocialNetworks(brand.brand?.socials || [])
  return {
    platforms: networks.slice(0, MAX_SOCIAL_PREVIEWS).map(network => ({
      network,
      icon: getSocialPlatformMonoIcon(network)
    })),
    hiddenCount: Math.max(0, networks.length - MAX_SOCIAL_PREVIEWS)
  }
}

// Adds what the row renders beyond the normalized brand
const withRowDisplayData = (brand, searchQuery) => ({
  ...brand,
  displayName: getBrandDisplayName(brand.name, brand.brand?.brand_names, searchQuery),
  socialsPreview: getSocialsPreview(brand),
  adDataStatus: getAdDataStatus(brand)
})

export default {
  name: 'SpyderSearchResults',
  components: {
    BrandAvatar,
    ChangeFolderIcon,
    ChangeFolderModal,
    ChevronRightIcon,
    DeleteIcon,
    FolderIcon,
    GlobeIcon,
    KebabMenuIcon,
    ViewIcon
  },
  mixins: [clickaway],
  props: {
    searchQuery: {
      type: String,
      required: true
    },
    // The user's subscribed brands, already normalized for the subscriptions list
    subscribedBrands: {
      type: Array,
      default: () => []
    },
    // Results page to show (synced with parent)
    page: {
      type: Number,
      default: 1
    }
  },
  data () {
    return {
      brandResults: [],
      brandResultsTotal: 0,
      currentPage: 1,
      loadingBrands: false,
      latestRequestId: 0,
      viewBrandIcon: SpyderIcon,

      // Brand options menu
      expandedBrandOptions: null, // Key of the brand whose menu is open
      loadingMenu: null, // Key of the brand being removed
      showChangeFolderModal: false,
      changeFolderBrand: null,

      // Horizontal scroll (shows the pinned columns' edge shadows)
      canScrollLeft: false,
      canScrollRight: false,
      scrollResizeObserver: null
    }
  },
  computed: {
    ...mapGetters('BoardsModule', ['getFolders']),
    subscriptionResults () {
      const query = this.searchQuery.toLowerCase()
      return this.subscribedBrands
        .filter(brand => brand.name?.toLowerCase().includes(query))
        .map(brand => withRowDisplayData(brand, this.searchQuery))
    },
    // Subscriptions are pinned above the first page of 'All brands' only
    showSubscriptions () {
      return this.subscriptionResults.length > 0
    },
    // Brands already listed under 'Your subscriptions' are left out of 'All brands'
    allBrandResults () {
      const subscriptionIds = this.subscriptionResults.map(brand => brand.id).filter(Boolean)
      return this.brandResults.filter(brand => !subscriptionIds.includes(brand.id))
    },
    // The search covers every brand, subscribed ones included, so its total is the overall count
    resultsCount () {
      return Math.max(this.brandResultsTotal, this.subscriptionResults.length)
    },
    totalPages () {
      return Math.min(Math.ceil(this.brandResultsTotal / PAGE_SIZE), MAX_PAGES)
    },
    // Page changes keep the current rows on screen (dimmed) until the next page arrives
    initialLoading () {
      return this.loadingBrands && !this.brandResults.length
    }
  },
  watch: {
    searchQuery: {
      immediate: true,
      handler () {
        this.brandResults = []
        this.brandResultsTotal = 0
        this.currentPage = 1
        this.fetchBrandResults(this.page)
      }
    },
    // e.g. back/forward to another page of the same search
    page (page) {
      if (page !== this.currentPage) this.fetchBrandResults(page)
    },
    currentPage (page) {
      this.$emit('update:page', page)
    }
  },
  mounted () {
    // The table's width changes with the window and as rows load, either of which can start or end the overflow
    this.scrollResizeObserver = new ResizeObserver(() => this.updateScrollState())
    this.scrollResizeObserver.observe(this.$refs.scrollContainer)
    this.scrollResizeObserver.observe(this.$refs.scrollContent)
  },
  beforeDestroy () {
    if (this.scrollResizeObserver) this.scrollResizeObserver.disconnect()
  },
  methods: {
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions', 'removeSpyderBrands']),
    async fetchBrandResults (requestedPage) {
      const page = Math.min(requestedPage, MAX_PAGES)
      // Drop responses from searches that have since been replaced
      const requestId = ++this.latestRequestId
      const previousPage = this.currentPage
      this.currentPage = page
      this.loadingBrands = true
      try {
        const { data: response } = await ForeplayAPI.Search.searchSpyderBrands(this.searchQuery, { page, limit: PAGE_SIZE })
        if (requestId !== this.latestRequestId) return
        // A stale or edited ?page= can point past the last page, so show the last page instead
        const lastPage = Math.max(1, Math.min(Math.ceil((response.total || 0) / PAGE_SIZE), MAX_PAGES))
        if (page > lastPage) return this.fetchBrandResults(lastPage)
        this.brandResults = normalizeBrandsForSubscriptionsList(response.data)
          .map(brand => withRowDisplayData(brand, this.searchQuery))
        this.brandResultsTotal = response.total || 0
      } catch (error) {
        if (requestId !== this.latestRequestId) return
        console.error(error)
        // Stay on the last page that loaded
        this.currentPage = previousPage
        this.$showAlert({
          type: 'error',
          message: 'Failed to search brands. See console for details.'
        })
      } finally {
        if (requestId === this.latestRequestId) this.loadingBrands = false
      }
    },
    updateScrollState () {
      const container = this.$refs.scrollContainer
      if (!container) return
      const scrollLeft = Math.round(container.scrollLeft)
      const maxScroll = Math.round(container.scrollWidth - container.clientWidth)
      this.canScrollLeft = scrollLeft > 0
      this.canScrollRight = scrollLeft < maxScroll
    },
    viewBrand (brand) {
      this.$router.push({ name: 'SpyderView', params: { id: brand.id } })
    },
    toggleBrandOptions (brand) {
      this.expandedBrandOptions = this.expandedBrandOptions === brand.key ? null : brand.key
    },
    // Every menu listens for outside clicks, so only the open one may close itself
    closeBrandOptions (brand) {
      if (this.expandedBrandOptions === brand.key) this.expandedBrandOptions = null
    },
    showChangeBrandFolderModal (brand) {
      this.changeFolderBrand = brand
      this.showChangeFolderModal = true
    },
    // Unsubscribes from a built brand, or cancels a pending one
    async removeSpyderBrand (brand) {
      try {
        this.loadingMenu = brand.key

        const { notCanceled } = await this.removeSpyderBrands([brand])
        if (notCanceled.length) {
          // It finished building first, so it's a subscription now
          await this.fetchSpyderSubscriptions()
          this.$showAlert({
            type: 'info',
            message: `${brand.name || 'This brand'} finished processing before it could be removed. Remove it again to unsubscribe.`
          })
          return
        }
        // The brand can still show up under 'All brands', now as one the user doesn't track
        if (!brand.pending) {
          this.brandResults = this.brandResults.map(result => result.id === brand.id
            ? { ...result, brand: { ...result.brand, is_subscribed: false } }
            : result)
        }
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to remove brand from Spyder. See console for details.'
        })
      } finally {
        this.loadingMenu = null
      }
    },
    getFolderName (brand) {
      return findFolderForBrand(this.getFolders, brand)?.name || 'Default Folder'
    },
    formatDate (date) {
      if (!date) return '—'
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return new Date(date).toLocaleDateString('en-US', options)
    }
  }
}
</script>

<style scoped>
.results-grid-template {
  display: grid;
  grid-template-columns: 200px 150px 180px 290px 160px 1fr;
  column-gap: 16px;
  align-items: center;
}

/* ============ HORIZONTAL SCROLL ============ */

/* Fills the card when there's room, and keeps every column at full width (scrolling) when there isn't */
.results-table {
  min-width: max-content;
}
.results-scroll-container::-webkit-scrollbar {
  height: 12px;
}
.results-scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(236, 239, 243, 1);
  border-radius: 10px;
  border: 3px solid #ffffff;
  cursor: pointer;
  transition: border-width 150ms ease-in-out;
}
.results-scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(223, 225, 231, 1);
  border-width: 2px;
}

/* Pinned columns: the brand on the left, the row actions on the right */
.sticky-start, .sticky-end {
  position: sticky;
  z-index: 2;
}
.sticky-start {
  left: 0;
}
.sticky-end {
  right: 0;
  /* Placed explicitly, since the label and divider rows leave the middle columns empty */
  grid-column: -2 / -1;
}
/* The divider line runs under the whole row, and the pinned cells redraw their stretch of it */
.section-divider {
  background: linear-gradient(#ECEFF3, #ECEFF3) center / calc(100% - 16px) 1px no-repeat;
}
.section-divider .sticky-start {
  background: linear-gradient(#ECEFF3, #ECEFF3) right center / calc(100% - 8px) 1px no-repeat white;
}
.section-divider .sticky-end {
  background: linear-gradient(#ECEFF3, #ECEFF3) left center / calc(100% - 8px) 1px no-repeat white;
}
/* The open menu has to sit above the pinned cells of the rows below it */
.sticky-end.menu-open {
  z-index: 3;
}
/* Edge shadows, shown only while there's content scrolled under the pinned column */
.sticky-start::after, .sticky-end::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 8px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease-in-out;
}
.sticky-start::after {
  left: 100%;
  background: linear-gradient(to right, rgba(4, 26, 75, 0.06), rgba(4, 26, 75, 0));
}
.sticky-end::before {
  right: 100%;
  background: linear-gradient(to left, rgba(4, 26, 75, 0.06), rgba(4, 26, 75, 0));
}
.can-scroll-left .sticky-start::after, .can-scroll-right .sticky-end::before {
  opacity: 1;
}

/* ============ BRAND OPTIONS MENU ============ */

.brand-menu-button {
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.brand-menu-button.active {
  background-color: #ECEFF3;
}
.brand-menu-button.inactive:hover {
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.brand-options {
  position: absolute;
  right: calc(100% + 4px);
  top: 50%;
  transform: translateY(-50%);
  width: max-content;
  min-width: 105px;
  z-index: 50;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.brand-options.open-up {
  top: auto;
  bottom: 0;
  transform: none;
}

.option-enter-active, .option-leave-active {
  transition: opacity 100ms ease-in-out;
}
.option-enter-from, .option-enter, .option-leave-to {
  opacity: 0;
}
.option-enter-to, .option-leave-from {
  opacity: 1;
}
</style>
